// POST /api/v1/admin/portfolios/:portfolio_id/creators — direct-assign
// an approved creator to a recruiting/stalled portfolio (no
// application). The server notifies the creator and respects their
// auto-assign opt-out + prior consent withdrawal.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type AssignCreatorOutcome = {
  portfolio_id: string;
  creator_user_id: string;
  added: boolean;
  display_name: string | null;
  handle: string | null;
};

export class AssignCreatorError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

async function assignCreator(input: {
  portfolio_id: string;
  creator_user_id: string;
}): Promise<AssignCreatorOutcome> {
  const response = await fetch(
    `/api/v1/admin/portfolios/${input.portfolio_id}/creators`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        // /api/v1/admin/portfolios is a FINANCIAL_MUTATION_PREFIX —
        // POSTs without this header 400 (IDEMPOTENCY_KEY_REQUIRED).
        "x-idempotency-key": crypto.randomUUID(),
      },
      body: JSON.stringify({ creator_user_id: input.creator_user_id }),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let code = "UNKNOWN";
    let message = `Assign failed (HTTP ${response.status})`;
    try {
      const body = (await response.json()) as {
        error?: { code?: string; message?: string };
      };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // non-JSON body
    }
    throw new AssignCreatorError(code, message);
  }
  const body = (await response.json()) as { data: AssignCreatorOutcome };
  return body.data;
}

export function useAssignCreatorToPortfolio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assignCreator,
    onSuccess: (outcome) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio", outcome.portfolio_id],
      });
    },
    onError: (error) => {
      if (error instanceof AssignCreatorError) {
        switch (error.code) {
          case "PORTFOLIO_CREATOR_OPTED_OUT":
            toast.error(
              "This creator opted out of being added without applying.",
            );
            break;
          case "PORTFOLIO_CONSENT_ALREADY_WITHDRAWN":
            toast.error(
              "This creator withdrew consent from this portfolio — they must re-apply themselves.",
            );
            break;
          case "PORTFOLIO_NOT_ASSIGNABLE":
            toast.error(
              "Creators can only be assigned while the portfolio is recruiting or stalled.",
            );
            break;
          case "PORTFOLIO_CREATOR_NOT_APPROVED":
            toast.error("That user isn't an approved creator.");
            break;
          default:
            toast.error(error.message);
        }
      } else {
        toast.error("Something went wrong. Try again.");
      }
    },
  });
}
